// viewAutomations.js
// The pure planning behind a View's "Keep current" dialog and panel: how a
// card's automation draft is seeded, which drafts would duplicate one another,
// and how the automations a View owns are reconciled to the cards it shows.
// No Firestore here — services/functionTriggers.js applies the plans.

import { sameRunParameters } from './functionParameterUtils';
import { DEFAULT_SCHEDULE_CONFIG, migrateTriggerParameters, viewTriggerId } from '../services/functionTriggers';

// WHEN a View's automations run is the View's, not the card's: every one is a
// daily 06:00 schedule — early enough that a morning report is waiting when
// the desk opens, late enough that overnight loads have landed. The dialog
// configures only WHAT each card runs with.
export const VIEW_SCHEDULE = { ...DEFAULT_SCHEDULE_CONFIG, hour: 6, minute: 0 };

// Each card has one deterministic automation document, scoped to its View.
export const triggerForCard = (viewId, instanceId, ownTriggers) => (
  (ownTriggers || []).find((trigger) => (
    trigger.id === viewTriggerId(viewId, instanceId)
    && trigger.managed_by?.type === 'view'
    && trigger.managed_by.view_id === viewId
    && trigger.managed_by.view_instance_id === instanceId
  )) || null
);

// What the dialog's tab for a card starts from: the automation it already has,
// or — for a card kept current for the first time — the card's current rail
// values, switched off until the person switches it on. The schedule is
// always the View's (VIEW_SCHEDULE).
export const seedCardDraft = (card, existing, viewName) => {
  if (existing) {
    const stale = existing.function_version !== card.func.publishedVersion;
    const saved = existing.parameters && typeof existing.parameters === 'object' ? existing.parameters : {};
    // A pin behind the published version means the saved filters describe an
    // older contract: they are carried onto the current one exactly as
    // enabling from the Automations tab does (mode-checked; the old version's
    // blocks are not loaded here). Removed filters remain absent for review.
    const parameters = stale
      ? migrateTriggerParameters(saved, card.func.data_source_blocks).parameters
      : saved;
    return {
      enabled: existing.enabled === true && !stale,
      name: existing.name || '',
      parameters,
      // The version the parameters describe. The save pins to THIS, so a
      // republish while the dialog is open aborts the save rather than
      // pinning filters written for one version to another.
      functionVersion: card.func.publishedVersion,
      stale,
      disabledReason: existing.enabled === true ? null : (existing.disabled_reason || null),
    };
  }
  return {
    enabled: false,
    name: `${viewName || 'View'} · ${card.label}`,
    parameters: card.railParameters || {},
    functionVersion: card.func.publishedVersion,
    stale: false,
    disabledReason: null,
  };
};

// Two switched-on cards of one function with identical parameters would run
// it twice for the same figures. Returns the offending pairs.
export const findDuplicateCards = (cards, drafts) => {
  const pairs = [];
  const on = (cards || []).filter((card) => drafts?.[card.instanceId]?.enabled);
  on.forEach((card, index) => {
    on.slice(index + 1).forEach((other) => {
      if (other.func.id !== card.func.id) return;
      if (!sameRunParameters(drafts[card.instanceId].parameters, drafts[other.instanceId].parameters)) return;
      pairs.push([card, other]);
    });
  });
  return pairs;
};

export const cardDraftChanged = (draft, baseline) => Boolean(draft) && (
  !baseline
  || Boolean(draft.enabled) !== Boolean(baseline.enabled)
  || draft.name !== baseline.name
  || draft.functionVersion !== baseline.functionVersion
  || !sameRunParameters(draft.parameters, baseline.parameters)
);

// Submit only changed cards. Every draft stays pinned to the version it was
// opened against, and unchanged cards remain entirely outside the write set.
// Switch all off explicitly includes every listed card, even an unchanged one.
export const plansFromDrafts = (cards, drafts, baselines = {}, includeUnchanged = false) => (cards || [])
  .filter((card) => drafts?.[card.instanceId]
    && (includeUnchanged || cardDraftChanged(drafts[card.instanceId], baselines[card.instanceId])))
  .map((card) => {
    const draft = drafts[card.instanceId];
    return {
      instanceId: card.instanceId,
      cardLabel: card.label,
      functionId: card.func.id,
      functionName: card.func.name,
      name: draft.name,
      triggerType: 'on_schedule',
      tableId: null,
      schedule: { ...VIEW_SCHEDULE },
      parameters: draft.parameters,
      functionVersion: draft.functionVersion ?? card.func.publishedVersion,
      enabled: Boolean(draft.enabled),
    };
  });

// Opening a View never creates or adopts an automation. The browser proposes
// orphan cleanup; the save transaction verifies departure against the current
// persisted View before deleting anything.
export const planReconcile = ({ viewId, cards, ownTriggers, instanceIds = null }) => {
  const present = new Set(instanceIds || (cards || []).map((card) => card.instanceId));
  const deleteIds = (ownTriggers || []).filter((trigger) => (
    trigger.managed_by?.type === 'view'
    && trigger.managed_by.view_id === viewId
    && !present.has(trigger.managed_by.view_instance_id)
  )).map((trigger) => trigger.id);
  return { deleteIds: Array.from(new Set(deleteIds)) };
};
